import { useState } from "react";
import { PenLine, MapPin, ShieldCheck, Loader2, Fingerprint } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { DictationButton } from "@/components/DictationButton";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

export function SignatureDialog({ open, onOpenChange, wsiId, demandeId, onSigned }) {
  const [step, setStep] = useState("geo"); // geo | totp
  const [geo, setGeo] = useState(null);
  const [totp, setTotp] = useState("");
  const [conclusion, setConclusion] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => { setStep("geo"); setGeo(null); setTotp(""); setConclusion(""); setResult(null); };

  const captureGeo = () => {
    if (!navigator.geolocation) { toast.error("Géolocalisation non disponible sur cet appareil"); return; }
    setLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGeo({ latitude: pos.coords.latitude, longitude: pos.coords.longitude, precision_m: pos.coords.accuracy });
        setStep("totp"); setLoading(false);
      },
      () => { toast.error("Position refusée — obligatoire pour signer"); setLoading(false); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const sign = async () => {
    if (totp.length < 6) { toast.error("Code TOTP à 6 chiffres requis"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/wsi/signatures", {
        wsi_id: wsiId, demande_id: demandeId, totp_code: totp,
        latitude: geo.latitude, longitude: geo.longitude, precision_m: geo.precision_m,
        conclusion,
      });
      setResult(data);
      toast.success("Lame signée numériquement");
      onSigned && onSigned();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent className="max-w-md" data-testid="wsi-signature-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PenLine className="h-5 w-5 text-teal-600" /> Signature numérique
          </DialogTitle>
          <DialogDescription>
            Signature PKI (démo) avec double facteur et géolocalisation — traçabilité Loi 25.
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="space-y-2 text-sm" data-testid="wsi-signature-result">
            <div className="flex items-center gap-2 text-teal-700 font-medium">
              <ShieldCheck className="h-5 w-5" /> Signature enregistrée
            </div>
            <div className="bg-slate-50 rounded-lg p-3 text-xs font-mono break-all text-slate-600">
              <div className="flex items-center gap-1.5 mb-1 text-slate-800"><Fingerprint className="h-3.5 w-3.5" /> Empreinte SHA-256</div>
              {result.pki?.empreinte}
            </div>
            <p className="text-xs text-slate-500">Certificat : {result.pki?.certificat}</p>
            <p className="text-xs text-slate-500">Horodatage : {new Date(result.created_at).toLocaleString("fr-CA")}</p>
            <button onClick={() => { onOpenChange(false); reset(); }}
                    className="mt-2 w-full py-2 bg-slate-900 text-white rounded-lg text-sm">Fermer</button>
          </div>
        ) : step === "geo" ? (
          <div className="space-y-3">
            <p className="text-sm text-slate-600">La position géographique est obligatoire pour signer depuis un appareil mobile.</p>
            <button onClick={captureGeo} disabled={loading} data-testid="wsi-sig-geo"
                    className="w-full py-2.5 bg-sky-600 text-white rounded-lg text-sm flex items-center justify-center gap-2 hover:bg-sky-700 disabled:opacity-60">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <MapPin className="h-4 w-4" />}
              Partager ma position
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center gap-1.5 text-xs text-teal-700">
              <MapPin className="h-3.5 w-3.5" /> Position captée (±{Math.round(geo.precision_m)} m)
            </div>
            <textarea value={conclusion} onChange={(e) => setConclusion(e.target.value)}
                      placeholder="Conclusion / note (optionnel)" rows={2}
                      data-testid="wsi-sig-conclusion"
                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" />
            <DictationButton value={conclusion} onChange={setConclusion} testId="dictation-wsi-conclusion" />
            <div>
              <label className="text-xs font-medium text-slate-500">Code d'authentification (TOTP)</label>
              <input value={totp} onChange={(e) => setTotp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                     inputMode="numeric" placeholder="000000" data-testid="wsi-sig-totp"
                     className="w-full mt-1 border border-slate-200 rounded-lg px-3 py-2 text-center text-lg tracking-[0.3em] font-mono" />
            </div>
            <button onClick={sign} disabled={loading} data-testid="wsi-sig-submit"
                    className="w-full py-2.5 bg-teal-600 text-white rounded-lg text-sm flex items-center justify-center gap-2 hover:bg-teal-700 disabled:opacity-60">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
              Signer la lame
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
